import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Save, Upload, Image as ImageIcon, X, MapPin } from "lucide-react";
import { LANDINGS_CONFIG } from "./AdminContent";

// Build editable groups (one per landing city) from shared LANDINGS_CONFIG
const GROUPS = LANDINGS_CONFIG.map((c) => ({
    slug: c.slug,
    label: c.label,
    fields: [
        { k: `${c.slug}.image`, l: "Imagem do hero (fundo)", type: "image", hint: "Recomendado 1920×1080. Fotografia da cidade/zona." },
        { k: `${c.slug}.subtitle`, l: "Subtítulo do hero", max: 300, area: true, d: c.def_sub },
        { k: `${c.slug}.why_intro`, l: `Parágrafo "Porquê em ${c.label}"`, max: 400, area: true, d: c.def_why },
        { k: `${c.slug}.cta_text`, l: "Texto do CTA final", max: 220, area: true, d: "Conte-nos o que pretende fazer. Analisamos consigo os próximos passos, sem compromisso." },
    ],
}));

const DEFAULTS = GROUPS.reduce((acc, g) => {
    g.fields.forEach(f => { if (f.type !== "image") acc[f.k] = f.d || ""; });
    return acc;
}, {});

export default function AdminLandings() {
    const [content, setContent] = useState({});
    const [dirty, setDirty] = useState({});
    const [activeSlug, setActiveSlug] = useState(GROUPS[0].slug);
    const [uploading, setUploading] = useState(null);

    useEffect(() => {
        api.get("/admin/content").then(r => {
            const raw = r.data || {};
            const dbMap = {};
            Object.entries(raw).forEach(([k, v]) => { if (v) dbMap[k] = v; });
            setContent({ ...DEFAULTS, ...dbMap });
        });
    }, []);

    const change = (k, v, max) => {
        const val = typeof max === "number" ? v.slice(0, max) : v;
        setContent(c => ({ ...c, [k]: val }));
        setDirty(d => ({ ...d, [k]: true }));
    };

    const uploadImage = async (k, file) => {
        if (!file) return;
        setUploading(k);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.put(`/admin/content/${encodeURIComponent(k)}`, { value: data.id });
            setContent(c => ({ ...c, [k]: data.id }));
            toast.success("Imagem carregada e publicada");
        } catch { toast.error("Falha no upload"); }
        finally { setUploading(null); }
    };

    const clearImage = async (k) => {
        if (!window.confirm("Remover imagem?")) return;
        await api.put(`/admin/content/${encodeURIComponent(k)}`, { value: "" });
        setContent(c => ({ ...c, [k]: "" }));
        toast.success("Imagem removida");
    };

    const save = async () => {
        const keys = Object.keys(dirty);
        try {
            await Promise.all(keys.map(k => api.put(`/admin/content/${encodeURIComponent(k)}`, { value: content[k] || "" })));
            toast.success(`${keys.length} texto(s) guardados`);
            setDirty({});
        } catch { toast.error("Erro ao guardar"); }
    };

    const active = useMemo(() => GROUPS.find(g => g.slug === activeSlug), [activeSlug]);

    return (
        <div className="max-w-4xl pb-20">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-stone-900">Landing pages</h1>
                    <p className="text-sm text-stone-500">Edite o conteúdo específico de cada uma das 6 páginas de localização SEO.</p>
                </div>
                <button onClick={save} disabled={Object.keys(dirty).length === 0} data-testid="landings-save"
                    className="btn-copper !py-2 !px-4 text-sm disabled:opacity-50">
                    <Save className="w-4 h-4" /> Guardar {Object.keys(dirty).length > 0 ? `(${Object.keys(dirty).length})` : ""}
                </button>
            </div>

            {/* City tabs */}
            <div className="flex flex-wrap gap-2 mb-6 border-b border-stone-200 pb-3">
                {GROUPS.map(g => (
                    <button key={g.slug} onClick={() => setActiveSlug(g.slug)} data-testid={`landing-tab-${g.slug}`}
                        className={`px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition ${activeSlug === g.slug
                            ? "bg-[hsl(var(--forest))] text-white"
                            : "bg-white border border-stone-200 text-stone-600 hover:border-[hsl(var(--forest))]"}`}>
                        <MapPin className="w-3.5 h-3.5" /> {g.label}
                    </button>
                ))}
            </div>

            <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-4">
                <div className="text-sm text-stone-500">A editar: <b>Landing page — {active.label}</b> · URL pública: <a href={`/${active.slug}`} target="_blank" rel="noreferrer" className="underline text-[hsl(var(--forest))]">/{active.slug}</a></div>
                {active.fields.map(f => {
                    if (f.type === "image") {
                        const v = content[f.k];
                        return (
                            <div key={f.k} className="border border-dashed border-stone-300 rounded-lg p-3 bg-stone-50/60">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> {f.l}</span>
                                    {f.hint && <span className="text-[11px] text-stone-400">{f.hint}</span>}
                                </div>
                                {v ? (
                                    <div className="relative w-full max-w-sm">
                                        <img src={/^https?:\/\//.test(v) ? v : fileUrl(v)} alt="" className="w-full aspect-[16/9] object-cover rounded-md" />
                                        <button type="button" onClick={() => clearImage(f.k)} data-testid={`landing-image-remove-${f.k}`}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow"><X className="w-3.5 h-3.5" /></button>
                                    </div>
                                ) : (
                                    <label className="flex flex-col items-center justify-center w-full max-w-sm aspect-[16/9] border-2 border-dashed border-stone-300 rounded-md cursor-pointer hover:border-[hsl(var(--copper))] transition bg-white"
                                        data-testid={`landing-image-upload-${f.k}`}>
                                        {uploading === f.k ? <span className="text-xs text-stone-500">A carregar…</span> : <><Upload className="w-5 h-5 text-stone-400 mb-2" /><span className="text-xs text-stone-500">Carregar imagem</span></>}
                                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadImage(f.k, e.target.files[0])} />
                                    </label>
                                )}
                            </div>
                        );
                    }
                    const val = content[f.k] ?? "";
                    const len = val.length;
                    const pct = f.max ? len / f.max : 0;
                    const color = pct >= 1 ? "text-red-600" : pct > 0.9 ? "text-orange-600" : "text-stone-400";
                    return (
                        <label key={f.k} className="block">
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs uppercase tracking-wider text-stone-500">{f.l}</span>
                                {f.max && <span className={`text-[11px] font-mono ${color}`}>{len}/{f.max}</span>}
                            </div>
                            {f.area
                                ? <textarea rows={3} value={val} maxLength={f.max} onChange={(e) => change(f.k, e.target.value, f.max)} className="w-full border border-stone-300 rounded p-2 text-sm" data-testid={`landing-${f.k}`} />
                                : <input value={val} maxLength={f.max} onChange={(e) => change(f.k, e.target.value, f.max)} className="w-full border border-stone-300 rounded p-2 text-sm" data-testid={`landing-${f.k}`} />}
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
